/**
 * Modal primitive — now on Radix Dialog (DESIGN-SPRINT-02, the one-pass
 * migration the old header comment promised).
 *
 * Same external API as before (open/onClose/title/subtitle/size/children), so
 * every existing consumer upgrades for free. What Radix adds over the
 * hand-rolled version:
 *   - a real FOCUS TRAP (Tab can no longer escape into the dimmed page — the
 *     A11Y-01d finding), and focus RETURNS to the trigger on close
 *   - portal stacking (nested dialogs layer correctly without z-index wars)
 *   - aria-modal / labelledby / describedby wired by construction
 *   - Esc + outside-click handled natively (the Pegasus close contract:
 *     outside-click + Esc + visible X — [[feedback_modal_close_pattern]])
 *
 * Motion: framer-motion via Radix forceMount so exit animations still play;
 * honors prefers-reduced-motion.
 *
 * New optional props for the bespoke-modal ports: `icon` (a small leading
 * chip in the header, e.g. the red Ban on Cancel) and `footer` (a pinned
 * action row under the scrollable body).
 */
import { useRef, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** ReactNode so rich headers (names + chips, e.g. MovePreview) fit. */
  title?: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
}

const sizeClasses = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
  xl: "max-w-5xl",
  /** SCHED-FLOW-01: the scheduling window — wide enough for a two-week grid + a summary rail. */
  "2xl": "max-w-[1320px]",
};

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  footer,
  children,
  size = "md",
}: ModalProps) {
  const reduceMotion = useReducedMotion();
  const bodyRef = useRef<HTMLDivElement>(null);
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm"
              />
            </Dialog.Overlay>
            <div className="pointer-events-none fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-16">
              <Dialog.Content
                asChild
                forceMount
                // Popovers that PORTAL to document.body (Google Places
                // .pac-container, our own suggestion lists marked
                // [data-stay-open]) live outside the dialog DOM — without this
                // guard, clicking a suggestion would read as an outside click
                // and close the modal.
                onPointerDownOutside={(e) => {
                  const t = e.target as HTMLElement | null;
                  if (t?.closest?.(".pac-container, [data-stay-open]"))
                    e.preventDefault();
                }}
                onInteractOutside={(e) => {
                  const t = e.target as HTMLElement | null;
                  if (t?.closest?.(".pac-container, [data-stay-open]"))
                    e.preventDefault();
                }}
                // KEYS-01 (Seth 2026-09-22): a window opens with the cursor in
                // its FIRST FIELD, not on the X. Radix would focus the first
                // focusable thing, which is the close button in the header —
                // so every form started with a wasted Tab. [data-autofocus]
                // wins; else the first real field; else the first button.
                // KEYS-01: Esc while a pick-list is open closes the LIST (its own
                // handler does that); only a second Esc closes the window.
                onEscapeKeyDown={(e) => {
                  const a = document.activeElement as HTMLElement | null;
                  if (a?.getAttribute("aria-expanded") === "true") e.preventDefault();
                }}
                onOpenAutoFocus={(e) => {
                  const body = bodyRef.current;
                  if (!body) return;
                  const pick =
                    body.querySelector<HTMLElement>("[data-autofocus]") ??
                    body.querySelector<HTMLElement>('input:not([type="hidden"]):not([readonly]):not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([readonly]):not([disabled]), [role="combobox"]') ??
                    body.querySelector<HTMLElement>('button:not([disabled]):not([tabindex="-1"]), a[href]');
                  if (pick) {
                    e.preventDefault();
                    pick.focus();
                    if (pick instanceof HTMLInputElement && pick.value && (pick.type === "text" || pick.type === "search")) pick.select();
                  }
                }}
              >
                <motion.div
                  initial={
                    reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.98 }
                  }
                  animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
                  exit={
                    reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8, scale: 0.98 }
                  }
                  transition={{ duration: 0.18 }}
                  className={cn(
                    "pointer-events-auto flex max-h-[calc(100vh-5rem)] w-full flex-col rounded-2xl bg-white shadow-2xl",
                    sizeClasses[size],
                  )}
                >
                  {/* Radix requires a Title for screen readers; keep it
                      invisible when the caller renders a headerless modal. */}
                  {title ? null : (
                    <Dialog.Title className="sr-only">Dialog</Dialog.Title>
                  )}
                  {(title || subtitle) && (
                    <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
                      <div className="flex min-w-0 items-start gap-2.5">
                        {icon}
                        <div className="min-w-0">
                          {title && (
                            <Dialog.Title className="text-base font-semibold text-slate-900">
                              {title}
                            </Dialog.Title>
                          )}
                          {subtitle && (
                            <Dialog.Description className="mt-0.5 text-xs text-slate-500">
                              {subtitle}
                            </Dialog.Description>
                          )}
                        </div>
                      </div>
                      <Dialog.Close asChild>
                        <button
                          type="button"
                          className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
                          aria-label="Close"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </Dialog.Close>
                    </div>
                  )}
                  <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                    {children}
                  </div>
                  {footer && (
                    <div className="border-t border-slate-200 px-6 py-3">{footer}</div>
                  )}
                </motion.div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
